import React from "react";

/**
 * Guard the sorter (and any long-form editor) against a mid-render crash from
 * a corrupt draft, a stale item ref, or an OOM after loading hundreds of photos.
 * Without this, a runtime exception unmounts the tree and the tab goes blank.
 */
export default class BulkSorterErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Surface to the console so we can diagnose from production without a
    // silent tab-blank. Also let the calling page log to a backend endpoint
    // later if we want telemetry.
    console.error("[BulkPhotoSorter crash]", error, info?.componentStack);
  }

  reset = () => {
    this.setState({ error: null });
    // Force one hard reload so IDB state re-loads cleanly. Draft is preserved
    // because we don't call clearDraft here.
    if (typeof window !== "undefined") window.location.reload();
  };

  render() {
    if (this.state.error) {
      return (
        <div className="max-w-2xl mx-auto p-10 space-y-6" data-testid="bulk-sorter-crash">
          <div className="bg-linen border border-red-700/30 p-8 space-y-4">
            <p className="editorial-label text-red-800">the sorter hit an unexpected error</p>
            <p className="text-sm text-espresso">
              your draft is safe in this browser&apos;s storage — reloading will pick up where you left off.
              if this keeps happening on the same batch, click <em>clear draft</em> after reloading and share the
              console error with support.
            </p>
            {this.state.error?.message && (
              <pre className="text-xs bg-ivory p-3 border border-taupe/40 overflow-x-auto font-mono text-charcoal">
                {String(this.state.error.message)}
              </pre>
            )}
            <button
              onClick={this.reset}
              data-testid="bulk-sorter-crash-reload"
              className="border border-brass text-brass hover:bg-brass hover:text-ivory px-4 py-2 text-xs tracking-widest uppercase"
            >
              reload sorter
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
